import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle, Send, ThumbsUp, Heart, Smile, Hand, Brain, Flame, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import MentionTextarea, { MentionText } from "@/components/MentionTextarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const REACTIONS = [
  { key: "thumbs_up", icon: ThumbsUp, emoji: "👍", label: "Like" },
  { key: "heart", icon: Heart, emoji: "❤️", label: "Love" },
  { key: "smile", icon: Smile, emoji: "😊", label: "Happy" },
  { key: "clap", icon: Hand, emoji: "👏", label: "Clap" },
  { key: "thinking", icon: Brain, emoji: "🤔", label: "Thinking" },
  { key: "fire", icon: Flame, emoji: "🔥", label: "Fire" },
];

export default function ActivityDiscussion({ activityId, activityTitle, user, onClose }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [activity, setActivity] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [newMentions, setNewMentions] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadDiscussions();
  }, [activityId]);

  useEffect(() => {
    if (activity?.course_id) {
      loadParticipants(activity.course_id);
    }
  }, [activity?.course_id]);

  const loadParticipants = async (courseId) => {
    try {
      const response = await fetch(`${API}/courses/${courseId}/participants`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setParticipants(await response.json());
      }
    } catch (error) {
      console.error('Failed to load participants');
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadDiscussions = async () => {
    try {
      const response = await fetch(`${API}/activities/${activityId}/discussions`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages);
        setActivity(data.activity);
      }
    } catch (error) {
      toast.error('Failed to load discussions');
    } finally {
      setLoading(false);
    }
  };

  const handlePostMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    setPosting(true);
    try {
      const response = await fetch(`${API}/activities/${activityId}/discussions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ message: newMessage.trim(), mentions: newMentions.map(m => m.user_id) })
      });

      if (response.ok) {
        setNewMessage("");
        setNewMentions([]);
        loadDiscussions();
        toast.success('Message posted!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to post message');
      }
    } catch (error) {
      toast.error('Failed to post message');
    } finally {
      setPosting(false);
    }
  };

  const handleReaction = async (messageId, emoji) => {
    try {
      const response = await fetch(`${API}/activity-discussions/${messageId}/reactions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ emoji })
      });

      if (response.ok) {
        loadDiscussions();
      }
    } catch (error) {
      toast.error('Failed to add reaction');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    
    try {
      const response = await fetch(`${API}/activity-discussions/${messageId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        loadDiscussions();
        toast.success('Message deleted');
      }
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const getReactionCount = (reactions, key) => {
    return reactions?.[key]?.length || 0;
  };

  const hasUserReacted = (reactions, key) => {
    return reactions?.[key]?.includes(user?.user_id);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatFullDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[85vh] flex flex-col bg-[#FEFAE0] border-[#CCD5AE] rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-[#CCD5AE] bg-gradient-to-r from-[#3A5A40] to-[#588157]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Merriweather, serif' }}>
                  Activity Discussion
                </h3>
                <p className="text-sm text-white/80 line-clamp-1">
                  {activityTitle || activity?.title || 'Discussion Board'}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-lg"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Info Banner */}
        <div className="px-4 py-3 bg-[#E9EDC9]/50 border-b border-[#CCD5AE]">
          <p className="text-sm text-[#588157] flex items-center gap-2">
            <span className="text-lg">💬</span>
            Share your thoughts, questions, and reflections about this activity with fellow students and teachers!
          </p>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ minHeight: '300px' }}>
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 animate-spin text-[#3A5A40]" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="text-4xl mb-3">🌱</div>
              <p className="text-[#588157] font-medium">No discussions yet</p>
              <p className="text-sm text-[#A3B18A]">Be the first to share your thoughts!</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.message_id}
                className={`flex gap-3 ${msg.user_id === user?.user_id ? 'flex-row-reverse' : ''}`}
                data-testid={`message-${msg.message_id}`}
              >
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {msg.user_picture ? (
                    <img
                      src={msg.user_picture}
                      alt={msg.user_name}
                      className="w-10 h-10 rounded-full object-cover border-2 border-[#CCD5AE]"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#3A5A40]/10 flex items-center justify-center">
                      <span className="text-sm font-bold text-[#3A5A40]">
                        {msg.user_name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Message Content */}
                <div className={`flex-1 max-w-[80%] ${msg.user_id === user?.user_id ? 'text-right' : ''}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-medium text-[#344E41] text-sm">{msg.user_name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      msg.user_role === 'teacher' ? 'bg-[#D4A373]/20 text-[#D4A373]' :
                      msg.user_role === 'admin' ? 'bg-[#3A5A40]/20 text-[#3A5A40]' :
                      'bg-[#588157]/20 text-[#588157]'
                    }`}>
                      {msg.user_role}
                    </span>
                    <span className="text-xs text-[#A3B18A] cursor-help" title={formatFullDateTime(msg.created_at)}>{formatTime(msg.created_at)}</span>
                  </div>
                  
                  <div className={`p-3 rounded-2xl ${
                    msg.user_id === user?.user_id 
                      ? 'bg-[#3A5A40] text-white rounded-tr-md' 
                      : 'bg-white border border-[#CCD5AE] text-[#344E41] rounded-tl-md'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap"><MentionText text={msg.message} mentions={msg.mentions} /></p>
                  </div>

                  {/* Reactions */}
                  <div className="flex items-center gap-1 mt-2 flex-wrap">
                    {REACTIONS.map((reaction) => {
                      const count = getReactionCount(msg.reactions, reaction.key);
                      const hasReacted = hasUserReacted(msg.reactions, reaction.key);
                      return (
                        <button
                          key={reaction.key}
                          onClick={() => handleReaction(msg.message_id, reaction.key)}
                          className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-all ${
                            hasReacted
                              ? 'bg-[#D4A373]/20 text-[#D4A373] border border-[#D4A373]/30'
                              : 'bg-[#E9EDC9]/50 text-[#588157] hover:bg-[#E9EDC9] border border-transparent'
                          }`}
                          title={reaction.label}
                        >
                          <span>{reaction.emoji}</span>
                          {count > 0 && <span>{count}</span>}
                        </button>
                      );
                    })}

                    {/* Delete button for own messages */}
                    {(msg.user_id === user?.user_id || user?.role === 'admin') && (
                      <button
                        onClick={() => handleDeleteMessage(msg.message_id)}
                        className="ml-2 text-xs text-[#A3B18A] hover:text-red-500 transition-colors"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <form onSubmit={handlePostMessage} className="p-4 border-t border-[#CCD5AE] bg-white/50">
          <div className="flex gap-2">
            <div className="flex-1">
              <MentionTextarea
                value={newMessage}
                onChange={setNewMessage}
                participants={participants}
                onMentionsChange={setNewMentions}
                currentUserId={user?.user_id}
                placeholder="Share your thoughts... Type @ to mention someone"
                rows={2}
                dataTestId="discussion-input"
                className="bg-white border-[#CCD5AE] focus:border-[#3A5A40] rounded-xl resize-none"
              />
            </div>
            <Button
              type="submit"
              disabled={posting || !newMessage.trim()}
              data-testid="send-message-button"
              className="self-end bg-[#3A5A40] text-white hover:bg-[#344E41] rounded-xl px-4"
            >
              {posting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
